/*
  Universal search. Adds online doctor routes, prescription prices and pharmacist consultation
  products to the Shopify predictive search dropdown, works out the search intent and lays the
  dropdown out for it:

    condition  "weight loss"   online doctor route leads, then medicine prices
    medicine   "wegovy"        the medicine card leads, with the two ways to get it
    consult    "viagra"        pharmacist consultation products lead (no prescription needed)
    symptom    "sore throat"   ask a pharmacist / GP leads, products beside it
    product    "vitamin d"     shop products lead

  Data: assets/hw-search-index.json (built by scripts/build-search-index.mjs). The Shopify groups
  (products, articles, pages) are rendered by sections/predictive-search.liquid and moved into the
  intent layout here. Results only link to pages, never add-to-cart.
*/
(function () {
  var indexUrl = document.currentScript && document.currentScript.dataset.index;
  var liveUrl = document.currentScript && document.currentScript.dataset.live;
  var MAX = 4;
  var GP_FROM = '39.00';
  var PHARMACIST_WHATSAPP = 'https://wa.me/35316853086';
  var indexPromise = null;

  // Shop group for condition and medicine searches. Shopify's predictive search is fuzzy
  // ("weight loss" returns hair-loss products, "weight" returns Tiger Balm White), so where
  // a smart collection fits (built on the custom.category metafield) its products are shown
  // instead. null hides the group where Shopify's matches are wrong and no collection fits.
  // Conditions not listed keep Shopify's results. Keys are condition names in the index.
  var SHOP = {
    'Weight Loss Treatment': 'weight-loss-support',
    'Hair Loss': 'hair-loss',
    'Hay Fever': 'allergies',
    'Stop Smoking': 'smoking-cessation',
    'Type 2 Diabetes': 'diabetes',
    'Asthma': null,
    'Period Delay': null,
    'Malaria Prevention': null
  };
  var collectionCache = {};

  var ICON = {
    whatsapp: '<svg class="hw-whatsapp-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="18" height="18">  <circle cx="12" cy="12" r="12" fill="#25D366"/>  <path fill="#ffffff" transform="translate(2.4 2.4) scale(0.8)" d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2Zm0 18.15c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 4.54 0 8.24 3.7 8.24 8.24 0 4.55-3.7 8.24-8.24 8.24Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z"/></svg>',
    doctor: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 3v6a5 5 0 0 0 10 0V3"/><path d="M11 14v2a5 5 0 0 0 10 0v-2"/><circle cx="21" cy="12" r="2"/></svg>',
    pill: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="8" rx="4" transform="rotate(-45 12 12)"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
    chat: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-8 8H8l-5 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8z"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 12 10 18 20 6"/></svg>',
    clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>',
    truck: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v9H3z"/><path d="M14 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>',
  };

  // Static index (conditions, GLP-1 page prices, a copy of the medicine list), then live
  // prices from Supabase via consult.healthwave.ie merged over it. The static copy is the
  // fallback when the live call fails, so a pricing outage never empties the search.
  function loadIndex() {
    if (!indexPromise) {
      indexPromise = fetch(indexUrl)
        .then(function (r) { return r.json(); })
        .then(function (index) { return liveUrl ? mergeLive(index) : index; })
        .catch(function () { indexPromise = null; return null; });
    }
    return indexPromise;
  }

  function mergeLive(index) {
    return fetch(liveUrl, { signal: AbortSignal.timeout ? AbortSignal.timeout(4000) : undefined })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (live) {
        if (!live || !live.rx || !live.rx.length) return index;
        var pages = {};
        index.rx.forEach(function (m) { if (m.url) pages[m.name.toLowerCase()] = m.url; });
        var seen = {};
        var rx = live.rx.map(function (m) {
          seen[m.name.toLowerCase()] = true;
          return { name: m.name, brand: m.brand || '', use: m.use || '', price: m.price, url: pages[m.name.toLowerCase()] };
        });
        // Medicines with their own price page that Supabase does not list (e.g. restricted ones)
        index.rx.forEach(function (m) { if (m.url && !seen[m.name.toLowerCase()]) rx.push(m); });
        index.rx = rx;
        if (live.consultations && live.consultations.length) index.consultations = live.consultations;
        return index;
      })
      .catch(function () { return index; });
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Escaped text with the first match of q wrapped in <mark>
  function hl(text, q) {
    var i = q ? text.toLowerCase().indexOf(q) : -1;
    if (i === -1) return esc(text);
    return esc(text.slice(0, i)) + '<mark>' + esc(text.slice(i, i + q.length)) + '</mark>' + esc(text.slice(i + q.length));
  }

  function has(text, q) {
    return text.toLowerCase().indexOf(q) !== -1;
  }

  function starts(text, q) {
    return text.toLowerCase().indexOf(q) === 0;
  }

  function euro(price) {
    var n = Number(price);
    return '€' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  /* ---------- matching ---------- */

  function search(index, q) {
    var none = { conditions: [], rx: [], rxTotal: 0, gp: false, consultations: [] };
    if (q.length < 2) return none;

    var consultations = (index.consultations || []).filter(function (c) {
      return has(c.name, q) || c.terms.some(function (t) { return has(t, q); });
    });
    var conditions = index.conditions.filter(function (c) {
      return has(c.name, q) || has(c.cat, q) || c.terms.some(function (t) { return has(t, q); });
    });
    var rx = index.rx.filter(function (m) {
      return has(m.name, q) || has(m.brand, q) || has(m.use, q);
    });
    // Medicine names first, then brand matches, then matches on the condition
    rx.sort(function (a, b) { return rank(a) - rank(b); });
    function rank(m) { return starts(m.name, q) ? 0 : has(m.name, q) || has(m.brand, q) ? 1 : 2; }

    var gp = !conditions.length && q.length >= 3 && index.gpTerms.some(function (t) {
      t = t.toLowerCase();
      return t.indexOf(q) !== -1 || q.indexOf(t) !== -1;
    });

    return { conditions: conditions, rx: rx, rxTotal: rx.length, gp: gp, consultations: consultations };
  }

  function classify(found, q, hasProducts) {
    if (found.consultations.length) return 'consult';
    var byName = found.rx.find(function (m) { return q.length >= 3 && (starts(m.name, q) || starts(m.brand, q)); });
    var conditionByName = found.conditions.some(function (c) { return starts(c.name, q); });
    if (byName && !conditionByName) return 'medicine';
    if (found.conditions.length) return 'condition';
    if (found.gp) return 'symptom';
    if (hasProducts) return 'product';
    return 'none';
  }

  /* ---------- markup ---------- */

  function option(id, inner) {
    return '<li id="' + id + '" class="predictive-search__list-item hw-ps__item" role="option" aria-selected="false">' + inner + '</li>';
  }

  function group(id, title, hint, items, extra) {
    return (
      '<div class="hw-ps__group" id="' + id + '">' +
        '<div class="hw-ps__group-head">' +
          '<h2 class="hw-ps__heading">' + title + '</h2>' +
          (hint ? '<span class="hw-ps__hint">' + hint + '</span>' : '') +
        '</div>' +
        '<ul class="hw-ps__list ' + (extra || '') + ' list-unstyled" role="group" aria-labelledby="' + id + '">' + items.join('') + '</ul>' +
      '</div>'
    );
  }

  function pill(text, tone) {
    return '<span class="hw-ps__pill' + (tone ? ' hw-ps__pill--' + tone : '') + '">' + esc(text) + '</span>';
  }

  // A result card: icon tile, type pill, title, one meta line, price
  function card(id, href, icon, pillText, titleHtml, meta, priceLabel, price, cls) {
    return option(id,
      '<a href="' + esc(href) + '" class="hw-ps__row hw-ps__row--card ' + (cls || '') + '" tabindex="-1">' +
        '<span class="hw-ps__icon">' + icon + '</span>' +
        '<span class="hw-ps__text">' +
          '<span class="hw-ps__title">' + (pillText ? pill(pillText) + ' ' : '') + titleHtml + '</span>' +
          (meta ? '<span class="hw-ps__meta">' + meta + '</span>' : '') +
        '</span>' +
        (price ? '<span class="hw-ps__price">' + (priceLabel ? '<small>' + priceLabel + '</small>' : '') + euro(price) + '</span>' : '') +
      '</a>'
    );
  }

  function doctorHero(c, q) {
    var chips = c.providers.slice(0, 4).map(function (p) { return '<span class="hw-ps__chip">' + esc(p) + '</span>'; }).join('');
    if (c.providers.length > 4) chips += '<span class="hw-ps__chip hw-ps__chip--muted">+' + (c.providers.length - 4) + ' more</span>';
    var href = '/pages/online-doctor?q=' + encodeURIComponent(c.name);
    return option('hw-usearch-doctor',
      '<a href="' + href + '" class="hw-ps__row hw-ps__hero" tabindex="-1">' +
        '<span class="hw-ps__hero-top">' +
          '<span class="hw-ps__icon hw-ps__icon--navy">' + ICON.doctor + '</span>' +
          '<span class="hw-ps__text">' +
            '<span class="hw-ps__title hw-ps__title--lg">' + pill('Online doctor') + ' ' + hl(c.name, q) + '</span>' +
            '<span class="hw-ps__meta">' + esc(c.types[0]) + (c.types.length > 1 ? ' or video' : '') + ' · prescription sent to Healthwave</span>' +
          '</span>' +
          (c.from ? '<span class="hw-ps__price"><small>Consultation from</small>' + euro(c.from) + '</span>' : '') +
        '</span>' +
        '<span class="hw-ps__hero-bottom">' +
          '<span class="hw-ps__chips">' + chips + '</span>' +
          '<span class="hw-ps__btn">Compare ' + c.routes + ' providers</span>' +
        '</span>' +
      '</a>'
    );
  }

  function medicineHero(m, q, doctor) {
    var orderHref = '/pages/carebot';
    var doctorHref = doctor ? '/pages/online-doctor?q=' + encodeURIComponent(doctor.name) : '/pages/online-doctor';
    var doctorFrom = doctor && doctor.from ? doctor.from : '20.00';
    return (
      option('hw-usearch-medicine',
        '<a href="' + esc(m.url || '/pages/prescription-prices?q=' + encodeURIComponent(m.name)) + '" class="hw-ps__row hw-ps__hero" tabindex="-1">' +
          '<span class="hw-ps__hero-top">' +
            '<span class="hw-ps__icon hw-ps__icon--navy">' + ICON.pill + '</span>' +
            '<span class="hw-ps__text">' +
              '<span class="hw-ps__title hw-ps__title--lg">' + pill('Prescription') + ' ' + hl(m.name, q) + '</span>' +
              '<span class="hw-ps__meta">' + hl(m.brand ? 'Also known as ' + m.brand + ' · ' + m.use : m.use, q) + '</span>' +
            '</span>' +
            '<span class="hw-ps__price"><small>Per month from</small>' + euro(m.price) + '</span>' +
          '</span>' +
        '</a>'
      ) +
      option('hw-usearch-order',
        '<a href="' + orderHref + '" class="hw-ps__row hw-ps__route" tabindex="-1">' +
          '<span class="hw-ps__icon">' + ICON.truck + '</span>' +
          '<span class="hw-ps__text"><span class="hw-ps__title">Have a prescription? Order ' + esc(m.name) + '</span><span class="hw-ps__meta">We dispense and deliver anywhere in Ireland</span></span>' +
        '</a>'
      ) +
      option('hw-usearch-doctor',
        '<a href="' + doctorHref + '" class="hw-ps__row hw-ps__route" tabindex="-1">' +
          '<span class="hw-ps__icon">' + ICON.doctor + '</span>' +
          '<span class="hw-ps__text"><span class="hw-ps__title">No prescription yet? See an online doctor</span><span class="hw-ps__meta">' + (doctor ? doctor.routes + ' providers' : 'Online form or video') + '</span></span>' +
          '<span class="hw-ps__price"><small>from</small>' + euro(doctorFrom) + '</span>' +
        '</a>'
      )
    );
  }

  function rxCards(list, q, limit) {
    return list.slice(0, limit).map(function (m, i) {
      var href = m.url || '/pages/prescription-prices?q=' + encodeURIComponent(m.name);
      var meta = hl(m.brand ? 'Also known as ' + m.brand + ' · ' + m.use : m.use, q);
      return card('hw-usearch-rx-' + i, href, ICON.pill, 'Prescription', hl(m.name, q), meta, 'per month from', m.price);
    });
  }

  function consultCards(list, q) {
    return list.slice(0, MAX).map(function (c, i) {
      return card('hw-usearch-consult-' + i, c.url, ICON.pill, 'No prescription', hl(c.name, q), esc(c.meta), 'from', c.from, 'hw-ps__row--tint');
    });
  }

  function pharmacistCard() {
    return option('hw-usearch-pharmacist',
      '<a href="' + PHARMACIST_WHATSAPP + '" class="hw-ps__row hw-ps__row--card hw-ps__row--tint" target="_blank" rel="noopener" tabindex="-1">' +
        '<span class="hw-ps__icon hw-ps__icon--wa">' + ICON.whatsapp + '</span>' +
        '<span class="hw-ps__text"><span class="hw-ps__title">Ask a Healthwave pharmacist</span><span class="hw-ps__meta">Message us on WhatsApp</span></span>' +
        '<span class="hw-ps__price">Free</span>' +
      '</a>'
    );
  }

  function gpCard() {
    return card('hw-usearch-gp', '/pages/online-doctor?q=GP', ICON.doctor, 'Online doctor', 'GP video or phone consult', 'WebDoctor · Eirdoc · same day', 'from', GP_FROM);
  }

  function facts() {
    return (
      '<div class="hw-ps__facts">' +
        '<span>' + ICON.check + 'No prescription needed</span>' +
        '<span>' + ICON.clock + '2-minute online questions</span>' +
        '<span>' + ICON.doctor + 'A pharmacist approves every order</span>' +
      '</div>'
    );
  }

  /* ---------- shop group from a collection ---------- */

  // Up to 4 in-stock products from a collection, cached per page view. Resolves null on failure.
  function collectionProducts(handle) {
    if (!collectionCache[handle]) {
      collectionCache[handle] = fetch('/collections/' + handle + '/products.json?limit=12')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (!d) return null;
          return d.products.filter(function (p) {
            return p.variants.some(function (v) { return v.available; });
          });
        })
        .catch(function () { collectionCache[handle] = null; return null; });
    }
    return collectionCache[handle];
  }

  // Same markup as the Shop group in sections/predictive-search.liquid
  function shopGroup(products, q) {
    var el = document.createElement('div');
    el.className = 'hw-ps__group';
    el.dataset.hwSrc = 'products';
    el.innerHTML =
      '<div class="hw-ps__group-head">' +
        '<h2 id="predictive-search-products" class="hw-ps__heading">Shop</h2>' +
        '<span class="hw-ps__hint">No prescription needed</span>' +
      '</div>' +
      '<ul class="hw-ps__list hw-ps__list--cards list-unstyled" role="group" aria-labelledby="predictive-search-products">' +
      products.map(function (p, i) {
        var prices = p.variants.map(function (v) { return Number(v.price); });
        var min = Math.min.apply(null, prices);
        var img = p.images && p.images[0] ? p.images[0].src : '';
        return '<li id="predictive-search-option-product-' + (i + 1) + '" class="predictive-search__list-item hw-ps__item" role="option" aria-selected="false">' +
          '<a href="/products/' + esc(p.handle) + '" class="hw-ps__row hw-ps__row--card hw-ps__row--product" tabindex="-1">' +
            '<span class="hw-ps__thumb">' + (img ? '<img src="' + esc(img + (img.indexOf('?') === -1 ? '?' : '&') + 'width=240') + '" alt="" width="56" height="56">' : '') + '</span>' +
            '<span class="hw-ps__text">' +
              '<span class="hw-ps__title">' + hl(p.title, q) + '</span>' +
              '<span class="hw-ps__meta">In stock</span>' +
            '</span>' +
            '<span class="hw-ps__price">' + (Math.max.apply(null, prices) > min ? 'from ' : '') + '€' + min.toFixed(2) + '</span>' +
          '</a>' +
        '</li>';
      }).join('') +
      '</ul>';
    return el;
  }

  // Resolves the Shop group to use: a collection group, false to hide it, or undefined to
  // keep Shopify's own group.
  function shopCondition(intent, found) {
    var c = found.conditions[0];
    return (intent === 'condition' || intent === 'medicine') && c && c.name in SHOP ? c : null;
  }

  function shopFor(intent, found, q) {
    var c = shopCondition(intent, found);
    if (!c) return Promise.resolve(undefined);
    if (SHOP[c.name] === null) return Promise.resolve(false);
    return collectionProducts(SHOP[c.name]).then(function (products) {
      if (!products) return undefined;
      return products.length ? shopGroup(products.slice(0, 4), q) : false;
    });
  }

  /* ---------- layout ---------- */

  // Returns { left: html, right: html, full: html, slots: {products, articles, pages} } per intent.
  // Slots name the column each Shopify group goes into, plus its heading override.
  function layout(intent, found, q, index) {
    var L = '', R = '', F = '';
    var slots = { products: 'right', articles: 'right', pages: 'right' };
    var order = ['articles', 'pages', 'products'];
    var after = '';
    var condition = found.conditions[0];

    if (intent === 'condition') {
      L += group('hw-usearch-treat', 'Get treatment', null, [doctorHero(condition, q)], 'hw-ps__list--cards');
      if (found.rx.length) L += group('hw-usearch-rx', 'Prescription medicines', 'Price per month, with a prescription', rxCards(found.rx, q, 3), 'hw-ps__list--cards');
      slots.products = 'right-compact';
    } else if (intent === 'medicine') {
      var hero = found.rx[0];
      L += group('hw-usearch-medicine-group', hl(hero.name, q), null, [medicineHero(hero, q, condition)], 'hw-ps__list--cards');
      var others = index.rx.filter(function (m) { return m !== hero && m.use.split(' · ').pop() === hero.use.split(' · ').pop(); });
      if (others.length) R += group('hw-usearch-compare', 'Compare', null, rxCards(others, q, 3).map(function (h) { return h; }), 'hw-ps__list--compact');
      slots.products = 'right-compact';
    } else if (intent === 'consult') {
      L += facts();
      L += group('hw-usearch-consult', 'Buy after a pharmacist consultation', null, consultCards(found.consultations, q), 'hw-ps__list--cards');
      var routes = rxCards(found.rx, q, 2);
      if (condition) routes.push(card('hw-usearch-doctor', '/pages/online-doctor?q=' + encodeURIComponent(condition.name), ICON.doctor, 'Online doctor', 'Prescription for higher doses', condition.routes + ' providers', 'from', condition.from));
      if (routes.length) R += group('hw-usearch-rx', 'With a prescription', null, routes, 'hw-ps__list--compact');
      slots.products = 'hidden';
    } else if (intent === 'symptom') {
      R += group('hw-usearch-help', 'Talk to someone', null, [pharmacistCard(), gpCard()], 'hw-ps__list--cards');
      R += '<p class="hw-ps__safety">Trouble breathing or swallowing: get urgent medical care.</p>';
      slots.products = 'left-grid';
      order = ['products', 'articles', 'pages'];
    } else if (intent === 'product') {
      slots.products = 'full-grid';
      slots.pages = 'full';
      slots.articles = 'full';
      order = ['products', 'articles', 'pages'];
      after = group('hw-usearch-help', 'Need advice?', null, [pharmacistCard()], 'hw-ps__list--cards');
    } else {
      slots.products = 'full';
      slots.pages = 'full';
      slots.articles = 'full';
      order = ['products', 'articles', 'pages'];
    }
    return { left: L, right: R, full: F, after: after, slots: slots, order: order };
  }

  function inject(instance) {
    var term = instance.getQuery();
    var q = term.toLowerCase();

    instance.querySelectorAll('.hw-ps [data-hw-hl]').forEach(function (el) {
      el.innerHTML = hl(el.textContent, q);
    });

    loadIndex().then(function (index) {
      if (!index || instance.getQuery() !== term) return;
      var body = instance.querySelector('[data-hw-body]');
      if (!body) return;

      var sources = {};
      body.querySelectorAll('[data-hw-src]').forEach(function (g) { sources[g.dataset.hwSrc] = g; });

      var found = search(index, q);
      var intent = classify(found, q, !!sources.products);
      // Hide Shopify's fuzzy products at once when they are about to be replaced
      if (sources.products && shopCondition(intent, found)) sources.products.remove();
      return shopFor(intent, found, q).then(function (shop) {
      if (instance.getQuery() !== term) return;
      if (shop === false) delete sources.products;
      else if (shop) sources.products = shop;
      var plan = layout(intent, found, q, index);

      body.className = 'hw-ps__body hw-ps__body--' + intent;
      body.innerHTML =
        '<div class="hw-ps__col hw-ps__col--left">' + plan.left + '</div>' +
        '<div class="hw-ps__col hw-ps__col--right">' + plan.right + '</div>' +
        '<div class="hw-ps__col hw-ps__col--full">' + plan.full + '</div>';

      var left = body.children[0], right = body.children[1], full = body.children[2];
      (plan.order || ['products', 'articles', 'pages']).forEach(function (key) {
        var g = sources[key];
        if (!g) return;
        var slot = plan.slots[key];
        if (slot === 'hidden') return;
        if (slot === 'right-compact') g.classList.add('hw-ps__group--compact');
        if (slot === 'right-grid' || slot === 'left-grid') g.classList.add('hw-ps__group--grid');
        if (slot === 'full-grid') g.classList.add('hw-ps__group--grid-wide');
        (slot.indexOf('full') === 0 ? full : slot.indexOf('right') === 0 ? right : left).appendChild(g);
      });
      if (plan.after) full.insertAdjacentHTML('beforeend', plan.after);
      if (!left.children.length) left.remove();
      if (!right.children.length) right.remove();
      if (!full.children.length) full.remove();
      });
    });

    // Home hero: bring the search box near the top so the results are in view
    if (instance.classList.contains('hw-hero__search-wrap')) {
      var top = instance.getBoundingClientRect().top;
      if (top > 140) window.scrollBy({ top: top - 100, behavior: 'smooth' });
    }
  }

  var PredictiveSearch = window.customElements && customElements.get('predictive-search');
  if (PredictiveSearch && indexUrl) {
    var render = PredictiveSearch.prototype.renderSearchResults;
    PredictiveSearch.prototype.renderSearchResults = function (markup) {
      render.call(this, markup);
      inject(this);
    };
    document.addEventListener('focusin', function onFocus(e) {
      if (e.target.closest && e.target.closest('predictive-search')) {
        loadIndex();
        document.removeEventListener('focusin', onFocus);
      }
    });
  }

  // Search results page (/search?q=): the treatment groups above the product results.
  // For a condition in SHOP, Shopify's fuzzy product cards (with their count, filters and
  // pagination) are replaced by the collection's products, as in the dropdown. The grid is
  // hidden until that is decided so the fuzzy cards do not flash.
  var page = document.querySelector('[data-hw-usearch-page]');
  if (page && indexUrl) {
    var pageTerm = page.getAttribute('data-term') || '';
    var grid = document.getElementById('ProductGridContainer');
    if (grid) grid.style.visibility = 'hidden';
    var reveal = function () { if (grid) grid.style.visibility = ''; };
    loadIndex().then(function (index) {
      if (!index) return reveal();
      var pageQ = pageTerm.trim().toLowerCase();
      var found = search(index, pageQ);
      var intent = classify(found, pageQ, false);
      var c = shopCondition(intent, found);
      var shop = c && SHOP[c.name] ? collectionProducts(SHOP[c.name]) : Promise.resolve(null);
      return shop.then(function (products) {
        if (c && (SHOP[c.name] === null || products)) replaceProducts();
        var plan = layout(intent, found, pageQ, index);
        var html = plan.left + plan.right + plan.full;
        if (products && products.length) {
          var g = shopGroup(products.slice(0, 8), pageQ);
          g.insertAdjacentHTML('beforeend', '<a class="hw-ps__see-all" href="/collections/' + esc(SHOP[c.name]) + '">View all products</a>');
          html += g.outerHTML;
        }
        reveal();
        if (!html) return;
        page.innerHTML = html;
        page.querySelectorAll('[role]').forEach(function (el) { el.removeAttribute('role'); el.removeAttribute('aria-selected'); });
        page.querySelectorAll('[tabindex]').forEach(function (el) { el.removeAttribute('tabindex'); });
        page.hidden = false;
      });
    }).catch(reveal);
  }

  // Removes Shopify's product cards from the search page, keeping article and page results
  function replaceProducts() {
    document.querySelectorAll('#product-grid .grid__item').forEach(function (li) {
      if (li.querySelector('.product-card-wrapper')) li.remove();
    });
    document.querySelectorAll('.facets-wrapper, facet-filters-form, .product-count, .product-count-vertical, .pagination-wrapper, .template-search__header [role="status"]').forEach(function (el) {
      el.hidden = true;
    });
    var grid = document.getElementById('ProductGridContainer');
    if (grid && !grid.querySelector('#product-grid .grid__item')) grid.hidden = true;
  }

  // Search chips (home hero) run their term in the search box of the same section
  document.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest('[data-hw-search-term]');
    var input = chip && chip.closest('section').querySelector('predictive-search input[type="search"]');
    if (!input) return;
    input.value = chip.getAttribute('data-hw-search-term');
    input.focus();
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });

  // Landing on the online doctor page from a result: /pages/online-doctor?q=Acne
  var q = new URLSearchParams(window.location.search).get('q');
  var doctorInput = q && document.getElementById('hw-search');
  if (doctorInput) {
    window.addEventListener('load', function () {
      doctorInput.value = q;
      doctorInput.dispatchEvent(new Event('input'));
    });
  }
})();
